import { useState, useEffect } from "react";
import { ChevronDown, ChevronUp, Banknote, CreditCard, Smartphone } from "lucide-react";
import { displayPrice, formatDate, getPaymentMethodLabel } from "@/lib/utils";
import { OrderPayment, PaymentMethodName } from "@/redux/services/pos/pos.type";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";

interface PaymentHistoryProps {
  payments: OrderPayment[];
  currency: string;
}

const getPaymentMethodIcon = (method: PaymentMethodName) => {
  switch (method) {
    case "cash":
      return <Banknote className="h-4 w-4 text-green-600" />;
    case "card":
      return <CreditCard className="h-4 w-4 text-blue-600" />;
    case "mobile money":
      return <Smartphone className="h-4 w-4 text-orange-600" />;
    default:
      return <Banknote className="h-4 w-4 text-muted-foreground" />;
  }
};

const PaymentHistory: React.FC<PaymentHistoryProps> = ({
  payments,
  currency,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  // Expand by default on desktop
  useEffect(() => {
    const mediaQuery = window.matchMedia("(min-width: 1024px)");
    setIsOpen(mediaQuery.matches);
  }, []);

  if (payments.length === 0) {
    return null;
  }

  return (
    <div className="bg-card rounded-lg border border-border shadow-sm p-4">
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CollapsibleTrigger asChild>
          <button className="flex items-center justify-between w-full">
            <h3 className="font-semibold text-card-foreground">
              Historique des paiements ({payments.length})
            </h3>
            {isOpen ? (
              <ChevronUp className="h-5 w-5 text-muted-foreground" />
            ) : (
              <ChevronDown className="h-5 w-5 text-muted-foreground" />
            )}
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="mt-3 divide-y divide-border">
            {payments.map((payment) => (
              <div key={payment.id} className="py-3 first:pt-0">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {getPaymentMethodIcon(payment.paymentMethod)}
                    <span className="text-sm font-medium text-card-foreground">
                      {getPaymentMethodLabel(payment.paymentMethod)}
                    </span>
                  </div>
                  <span className="text-sm font-semibold text-card-foreground">
                    {displayPrice(payment.amount, currency)}
                  </span>
                </div>
                <div className="mt-1 ml-6">
                  <p className="text-xs text-muted-foreground">
                    {formatDate(new Date(payment.createdAt))}
                  </p>
                  {payment.paymentMethod === "cash" &&
                    payment.cashTendered != null && (
                      <div className="mt-1 text-xs text-muted-foreground">
                        <span>
                          Reçu: {displayPrice(payment.cashTendered, currency)}
                        </span>
                        {payment.cashChange != null && (
                          <span className="ml-3">
                            Rendu: {displayPrice(payment.cashChange, currency)}
                          </span>
                        )}
                      </div>
                    )}
                  {payment.tipAmount != null && payment.tipAmount > 0 && (
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Pourboire: {displayPrice(payment.tipAmount, currency)}
                    </p>
                  )}
                  {payment.transactionReference && (
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Réf: {payment.transactionReference}
                    </p>
                  )}
                  {payment.processedBy &&
                    (payment.processedBy.fullName ?? payment.processedBy.email) && (
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Par: {payment.processedBy.fullName ?? payment.processedBy.email}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
};

export default PaymentHistory;
